import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { opportunitiesService, projectsService, tendersService } from '../../services/partnerService';
import { supabase } from '../../lib/supabase';
import { format, isAfter, addDays, parseISO } from 'date-fns';

// ─── Constants ────────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  opportunity: {
    label: 'Joint Opportunity',
    color: 'bg-violet-100 text-violet-700',
    dot: 'bg-violet-500',
    border: 'border-l-violet-500',
    kanbanKey: 'opp_status',
  },
  project: {
    label: 'Active Project',
    color: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-500',
    border: 'border-l-emerald-500',
    kanbanKey: 'project_status',
  },
  tender: {
    label: 'RFQ & Tender',
    color: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    border: 'border-l-amber-500',
    kanbanKey: 'tender_status',
  },
};

const STATUS_COLORS = {
  Identified: 'bg-slate-100 text-slate-600',
  Qualified: 'bg-blue-100 text-blue-700',
  Proposal: 'bg-yellow-100 text-yellow-700',
  Negotiation: 'bg-orange-100 text-orange-700',
  Won: 'bg-green-100 text-green-700',
  Lost: 'bg-red-100 text-red-700',
  'In Progress': 'bg-blue-100 text-blue-700',
  Completed: 'bg-green-100 text-green-700',
  'On Hold': 'bg-yellow-100 text-yellow-700',
  Cancelled: 'bg-red-100 text-red-700',
  Open: 'bg-sky-100 text-sky-700',
  Submitted: 'bg-indigo-100 text-indigo-700',
  Awarded: 'bg-green-100 text-green-700',
  'Not Awarded': 'bg-red-100 text-red-700',
  Closed: 'bg-slate-100 text-slate-600',
  Planning: 'bg-purple-100 text-purple-700',
};

const KANBAN_COLUMNS = [
  { key: 'pipeline', label: 'Pipeline', statuses: ['Identified', 'Qualified', 'Open', 'Planning'] },
  { key: 'in_progress', label: 'In Progress', statuses: ['In Progress', 'Negotiation', 'Proposal'] },
  { key: 'submitted', label: 'Proposal', statuses: ['Submitted', 'On Hold'] },
  { key: 'won', label: 'Won', statuses: ['Won', 'Completed', 'Awarded'] },
  { key: 'lost', label: 'Lost', statuses: ['Lost', 'Cancelled', 'Not Awarded', 'Closed'] },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getRecordStatus = (rec) =>
  rec?.opp_status || rec?.project_status || rec?.tender_status || '—';

const getRecordDate = (rec) =>
  rec?.expected_close || rec?.end_date || rec?.submission_deadline || rec?.due_date || null;

const formatCurrency = (v) => {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('en-OM', { style: 'currency', currency: 'OMR', maximumFractionDigits: 0 })?.format(v);
};

const isUrgent = (dateStr) => {
  if (!dateStr) return false;
  try {
    const d = parseISO(dateStr);
    const now = new Date();
    return isAfter(addDays(now, 7), d) && isAfter(d, now);
  } catch { return false; }
};

// ─── Stats Bar ────────────────────────────────────────────────────────────────
const StatsBar = ({ records }) => {
  const total = records?.length || 0;
  const totalValue = records?.reduce((s, r) => s + (parseFloat(r?.value || r?.estimated_value || 0)), 0);
  const activeProjects = records?.filter(r => r?.type === 'project' && r?.project_status === 'In Progress')?.length || 0;
  const openTenders = records?.filter(r => r?.type === 'tender' && (r?.tender_status === 'Open' || r?.tender_status === 'Submitted'))?.length || 0;
  const wonOpps = records?.filter(r => r?.type === 'opportunity' && r?.opp_status === 'Won')?.length || 0;

  const stats = [
    { label: 'Total Engagements', value: total, icon: 'Database', color: 'from-violet-500 to-purple-600', bg: 'bg-violet-50' },
    { label: 'Total Pipeline Value', value: formatCurrency(totalValue), icon: 'DollarSign', color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50' },
    { label: 'Active Projects', value: activeProjects, icon: 'Briefcase', color: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50' },
    { label: 'Open Tenders', value: openTenders, icon: 'FileText', color: 'from-amber-500 to-orange-600', bg: 'bg-amber-50' },
    { label: 'Won Opportunities', value: wonOpps, icon: 'Trophy', color: 'from-green-500 to-emerald-600', bg: 'bg-green-50' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
      {stats?.map((s) => (
        <div key={s?.label} className={`${s?.bg} rounded-2xl p-4 border border-white/60 shadow-sm`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s?.color} flex items-center justify-center shadow-sm`}>
              <Icon name={s?.icon} size={16} className="text-white" />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">{s?.value}</div>
          <div className="text-xs text-slate-500 mt-1 font-medium">{s?.label}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Type Badge ───────────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG?.[type];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg?.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
      {cfg?.label}
    </span>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cls = STATUS_COLORS?.[status] || 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>
      {status || '—'}
    </span>
  );
};

// ─── Table View ───────────────────────────────────────────────────────────────
const TableView = ({ records, onRowClick }) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    return [...(records || [])]?.sort((a, b) => {
      let av = a?.[sortField] || '';
      let bv = b?.[sortField] || '';
      if (typeof av === 'string') av = av?.toLowerCase();
      if (typeof bv === 'string') bv = bv?.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [records, sortField, sortDir]);

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={13}
      className="text-slate-400"
    />
  );

  const ThBtn = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-slate-900 transition-colors"
    >
      {children}
      <SortIcon field={field} />
    </button>
  );

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200/60 shadow-sm bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/80">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <ThBtn field="type">Type</ThBtn>
            </th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <ThBtn field="name">Name</ThBtn>
            </th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Partner</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <ThBtn field="value">Value</ThBtn>
            </th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Ownership</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Due Date</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted?.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-16 text-slate-400">
                <Icon name="Database" size={32} className="mx-auto mb-3 opacity-30" />
                <div className="text-sm font-medium">No records found</div>
              </td>
            </tr>
          )}
          {sorted?.map((rec) => {
            const dueDate = getRecordDate(rec);
            const urgent = isUrgent(dueDate);
            const partnerName = rec?.partners?.brand_name || rec?.partners?.legal_name || '—';
            const value = rec?.value || rec?.estimated_value;
            return (
              <tr
                key={`${rec?.type}-${rec?.id}`}
                className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors cursor-pointer border-l-2 ${TYPE_CONFIG?.[rec?.type]?.border || ''}`}
                onClick={() => onRowClick(rec)}
              >
                <td className="px-4 py-3">
                  <TypeBadge type={rec?.type} />
                </td>
                <td className="px-4 py-3">
                  <span className="font-semibold text-slate-900 hover:text-primary transition-colors line-clamp-1">
                    {rec?.name || rec?.title || '—'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-700 text-sm">{partnerName}</span>
                    {rec?.partners?.tier && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
                        {rec?.partners?.tier}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-semibold text-slate-800">
                  {value ? formatCurrency(value) : '—'}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={getRecordStatus(rec)} />
                </td>
                <td className="px-4 py-3 text-slate-600 text-sm">
                  {rec?.owned_by || rec?.ownership || '—'}
                </td>
                <td className="px-4 py-3">
                  {dueDate ? (
                    <div className={`flex items-center gap-1.5 text-sm ${urgent ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                      {urgent && <Icon name="AlertTriangle" size={13} className="text-red-500" />}
                      {format(parseISO(dueDate), 'MMM d, yyyy')}
                    </div>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => { e?.stopPropagation(); onRowClick(rec); }}
                    className="p-1.5 rounded-lg hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                    title="View Profile"
                  >
                    <Icon name="ExternalLink" size={15} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Kanban Card ──────────────────────────────────────────────────────────────
const KanbanCard = ({ rec, onClick }) => {
  const dueDate = getRecordDate(rec);
  const urgent = isUrgent(dueDate);
  const value = rec?.value || rec?.estimated_value;
  const partnerName = rec?.partners?.brand_name || rec?.partners?.legal_name;

  return (
    <div
      onClick={() => onClick(rec)}
      className={`bg-white rounded-xl border border-slate-200/60 p-3 shadow-sm hover:shadow-md transition-all cursor-pointer border-l-4 ${TYPE_CONFIG?.[rec?.type]?.border || ''} w-full`}
    >
      <div className="mb-2">
        <TypeBadge type={rec?.type} />
      </div>
      <div className="font-semibold text-slate-900 text-sm leading-snug mb-2 break-words">
        {rec?.name || rec?.title || '—'}
      </div>
      {partnerName && (
        <div className="text-xs text-slate-500 mb-1 truncate">{partnerName}</div>
      )}
      <div className="flex items-center justify-between mt-1">
        {value ? (
          <span className="text-xs font-semibold text-slate-700">{formatCurrency(value)}</span>
        ) : (
          <span />
        )}
        {urgent && <Icon name="AlertTriangle" size={13} className="text-red-500 flex-shrink-0" />}
      </div>
      {dueDate && (
        <div className={`mt-2 text-xs flex items-center gap-1 ${urgent ? 'text-red-500 font-medium' : 'text-slate-400'}`}>
          <Icon name="Calendar" size={11} />
          {format(parseISO(dueDate), 'MMM d, yyyy')}
        </div>
      )}
    </div>
  );
};

// ─── Kanban View ──────────────────────────────────────────────────────────────
const KanbanView = ({ records, onCardClick }) => {
  const columns = useMemo(() => {
    return KANBAN_COLUMNS?.map(col => ({
      ...col,
      cards: records?.filter(r => {
        const status = getRecordStatus(r);
        return col?.statuses?.includes(status);
      }) || [],
    }));
  }, [records]);

  return (
    <div className="grid grid-cols-5 gap-3 w-full">
      {columns?.map(col => (
        <div key={col?.key} className="min-w-0 flex flex-col">
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wide truncate">{col?.label}</h3>
            <span className="text-xs font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex-shrink-0 ml-1">
              {col?.cards?.length}
            </span>
          </div>
          <div className="flex flex-col gap-2 min-h-[120px] bg-slate-50/60 rounded-xl p-2 border border-slate-100 flex-1">
            {col?.cards?.length === 0 && (
              <div className="text-center py-8 text-slate-300 text-xs">Empty</div>
            )}
            {col?.cards?.map(rec => (
              <KanbanCard key={`${rec?.type}-${rec?.id}`} rec={rec} onClick={onCardClick} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const EngagementRecordsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [allRecords, setAllRecords] = useState([]);
  const [partners, setPartners] = useState([]);

  // Filters — pre-apply from location state if provided
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(() => location?.state?.statusFilter || 'all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // View
  const [viewMode, setViewMode] = useState('table'); // table | kanban

  // ─── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [opps, projs, tenders] = await Promise.all([
        opportunitiesService?.getAll(),
        projectsService?.getAll(),
        tendersService?.getAll(),
      ]);

      const tagged = [
        ...(opps || [])?.map(r => ({ ...r, type: 'opportunity' })),
        ...(projs || [])?.map(r => ({ ...r, type: 'project' })),
        ...(tenders || [])?.map(r => ({ ...r, type: 'tender' })),
      ];

      // Sort by created_at desc
      tagged?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
      setAllRecords(tagged);

      // Collect unique partners
      const { data: partnerData } = await supabase?.from('partners')?.select('id, legal_name, brand_name, tier')?.order('legal_name');
      setPartners(partnerData || []);
    } catch (err) {
      console.error('Failed to fetch engagement records:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Filtered Records ───────────────────────────────────────────────────────
  const filteredRecords = useMemo(() => {
    return allRecords?.filter(r => {
      if (typeFilter !== 'all' && r?.type !== typeFilter) return false;

      const status = getRecordStatus(r);
      // 'active_pipeline' is a special filter: exclude Won/Lost records
      if (statusFilter === 'active_pipeline') {
        if (['Won', 'Lost', 'Completed', 'Cancelled', 'Not Awarded', 'Closed', 'Awarded']?.includes(status)) return false;
      } else if (statusFilter !== 'all' && status !== statusFilter) return false;

      if (partnerFilter !== 'all' && r?.partner_id !== partnerFilter) return false;

      if (searchQuery) {
        const q = searchQuery?.toLowerCase();
        const name = (r?.name || r?.title || '')?.toLowerCase();
        const sector = (r?.sector || '')?.toLowerCase();
        const client = (r?.client || '')?.toLowerCase();
        const partnerName = (r?.partners?.brand_name || r?.partners?.legal_name || '')?.toLowerCase();
        if (!name?.includes(q) && !sector?.includes(q) && !client?.includes(q) && !partnerName?.includes(q)) return false;
      }

      const dueDate = getRecordDate(r);
      if (fromDate && dueDate && dueDate < fromDate) return false;
      if (toDate && dueDate && dueDate > toDate) return false;

      return true;
    });
  }, [allRecords, typeFilter, statusFilter, partnerFilter, searchQuery, fromDate, toDate]);

  // ─── All statuses for dropdown ──────────────────────────────────────────────
  const allStatuses = useMemo(() => {
    const set = new Set();
    allRecords?.forEach(r => { const s = getRecordStatus(r); if (s && s !== '—') set?.add(s); });
    return Array.from(set)?.sort();
  }, [allRecords]);

  // ─── Navigate to profile ────────────────────────────────────────────────────
  const handleRecordClick = (rec) => {
    if (rec?.type === 'opportunity') navigate(`/engagement-management-hub/opportunity/${rec?.id}`, { state: { opportunity: rec } });
    else if (rec?.type === 'project') navigate(`/engagement-management-hub/project/${rec?.id}`, { state: { project: rec } });
    else if (rec?.type === 'tender') navigate(`/engagement-management-hub/tender/${rec?.id}`, { state: { tender: rec } });
  };

  const activeFiltersCount = [
    typeFilter !== 'all',
    statusFilter !== 'all',
    partnerFilter !== 'all',
    !!searchQuery,
    !!fromDate,
    !!toDate,
  ]?.filter(Boolean)?.length;

  const clearFilters = () => {
    setTypeFilter('all');
    setStatusFilter('all');
    setPartnerFilter('all');
    setSearchQuery('');
    setFromDate('');
    setToDate('');
  };

  return (
    <>
      <Helmet>
        <title>Engagement Records | PartnerHub</title>
      </Helmet>
      <Header />
      <div className="min-h-screen bg-slate-50 pt-[148px]">
        <div className="max-w-[1600px] mx-auto px-6 py-6">

          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Icon name="Database" size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Engagement Records</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Unified view of all Joint Opportunities, Active Projects & RFQs/Tenders
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconSize={15}
                onClick={fetchAll}
                className="text-slate-600"
              >
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowRight"
                iconSize={15}
                onClick={() => navigate('/engagement-management-hub')}
                className="text-slate-600"
              >
                Go to Engagement Hub
              </Button>
            </div>
          </div>

          {/* Stats Bar */}
          <StatsBar records={allRecords} />

          {/* Main Content */}
          <div className="flex gap-5">
            {/* Left: Filters + Table/Kanban */}
            <div className="flex-1 min-w-0">

              {/* Filter Bar */}
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-4 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Search */}
                  <div className="relative flex-1 min-w-[200px]">
                    <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e?.target?.value)}
                      placeholder="Search by name, sector, partner..."
                      className="w-full pl-9 pr-4 h-9 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all placeholder-slate-300"
                    />
                  </div>

                  {/* Type Filter */}
                  <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                    {[
                      { key: 'all', label: 'All' },
                      { key: 'opportunity', label: 'Opportunities' },
                      { key: 'project', label: 'Projects' },
                      { key: 'tender', label: 'Tenders' },
                    ]?.map(t => (
                      <button
                        key={t?.key}
                        onClick={() => setTypeFilter(t?.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          typeFilter === t?.key
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {t?.label}
                      </button>
                    ))}
                  </div>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e?.target?.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white text-slate-700"
                  >
                    <option value="all">All Statuses</option>
                    {allStatuses?.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>

                  {/* Partner Filter */}
                  <select
                    value={partnerFilter}
                    onChange={e => setPartnerFilter(e?.target?.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white text-slate-700 max-w-[180px]"
                  >
                    <option value="all">All Partners</option>
                    {partners?.map(p => (
                      <option key={p?.id} value={p?.id}>
                        {p?.brand_name || p?.legal_name}
                      </option>
                    ))}
                  </select>

                  {/* Date Range */}
                  <input
                    type="date"
                    value={fromDate}
                    onChange={e => setFromDate(e?.target?.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white text-slate-700"
                    title="From date"
                  />
                  <input
                    type="date"
                    value={toDate}
                    onChange={e => setToDate(e?.target?.value)}
                    className="h-9 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white text-slate-700"
                    title="To date"
                  />

                  {/* Clear Filters */}
                  {activeFiltersCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      <Icon name="X" size={12} />
                      Clear ({activeFiltersCount})
                    </button>
                  )}

                  {/* View Toggle */}
                  <div className="ml-auto flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-1.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
                      title="Table view"
                    >
                      <Icon name="Table" size={15} />
                    </button>
                    <button
                      onClick={() => setViewMode('kanban')}
                      className={`p-1.5 rounded-lg transition-all ${viewMode === 'kanban' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
                      title="Kanban view"
                    >
                      <Icon name="Columns" size={15} />
                    </button>
                  </div>
                </div>

                {/* Results count */}
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <Icon name="Filter" size={12} />
                  Showing <span className="font-semibold text-slate-700">{filteredRecords?.length}</span> of{' '}
                  <span className="font-semibold text-slate-700">{allRecords?.length}</span> records
                </div>
              </div>

              {/* Table / Kanban */}
              {loading ? (
                <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm flex items-center justify-center py-24">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                    <div className="text-sm text-slate-500 font-medium">Loading engagement records…</div>
                  </div>
                </div>
              ) : viewMode === 'table' ? (
                <TableView records={filteredRecords} onRowClick={handleRecordClick} />
              ) : (
                <KanbanView records={filteredRecords} onCardClick={handleRecordClick} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EngagementRecordsPage;
